import { memo, useMemo, type ReactNode } from 'react';

import { Cards, Chip } from '@components';
import { SCREEN_BREAKPOINTS } from '@constants';
import { VariantType } from '@enums';
import { useMediaQuery } from '@hooks';
import type { Options } from '@types';
import { cn } from '@utils';

import { FieldDescription, FieldLabel } from './components';

export interface FieldCardProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  besideTitle?: boolean;
  showChip?: boolean;
  selectedInput?: Options;
  rightColumnClassName?: string;
  className?: string;
  'data-testid'?: string;
}

export const FieldCard = memo<FieldCardProps>(
  ({
    title,
    description,
    children,
    besideTitle = false,
    showChip = false,
    selectedInput,
    rightColumnClassName = 'max-w-64',
    className,
    'data-testid': testId,
  }) => {
    const isAboveSm = useMediaQuery(`(min-width: ${SCREEN_BREAKPOINTS.sm}px)`);

    const selectedLabels = useMemo<(string | number)[]>(() => {
      if (!selectedInput?.length) return [];

      return selectedInput.map((input): string | number => {
        if (typeof input === 'object' && input !== null && 'label' in input) {
          return input.label;
        }
        return input;
      });
    }, [selectedInput]);

    const shouldShowChips = useMemo(() => showChip && selectedLabels.length > 0, [showChip, selectedLabels.length]);

    const cardClasses = cn(
      'flex',
      besideTitle ? 'flex-col' : 'flex-col sm:flex-row',
      !children && 'overflow-hidden',
      'border border-gray-200/80 bg-white rounded-2xl',
      'shadow-sm hover:shadow-md transition-all duration-200 ease-in-out',
      className,
    );

    const contentClasses = cn(
      'flex flex-col',
      'px-5 py-6 sm:px-6 sm:py-7 lg:px-8 lg:py-8',
      'space-y-4 sm:space-y-5',
      besideTitle ? 'sm:flex-1' : 'flex-1',
    );

    const headerClasses = cn(
      'flex gap-4',
      besideTitle ? 'items-start justify-between sm:items-center' : 'flex-col space-y-3',
    );

    const titleSectionClasses = cn('flex-1 min-w-0', besideTitle ? 'space-y-2' : 'space-y-3');

    const controlContainerClasses = cn(
      'flex items-center justify-center flex-shrink-0',
      'min-w-12 min-h-10',
      besideTitle && 'ml-4 sm:ml-6',
      'self-start sm:self-center',
    );

    const sidePanelClasses = cn(
      'flex flex-col w-full justify-center items-center',
      'px-5 pb-6 sm:px-6 sm:py-7 lg:px-8 lg:py-8',
      'sm:bg-slate-50/50 sm:border-l sm:border-gray-200 sm:shadow-sm',
      'rounded-b-2xl sm:rounded-r-2xl sm:rounded-bl-none',
      isAboveSm && rightColumnClassName,
      'transition-all duration-200 ease-in-out',
    );

    return (
      <Cards rounded="rounded-2xl" withoutPadding className={cardClasses} data-testid={testId}>
        {/* Main Content Section */}
        <div className={contentClasses}>
          <div className={headerClasses}>
            {/* Label and Description Section */}
            <div className={titleSectionClasses}>
              <FieldLabel title={title} testId={testId} />
              <FieldDescription description={description} />
            </div>

            {/* Form Control beside title (typically switches on mobile) */}
            {besideTitle && children && <div className={controlContainerClasses}>{children}</div>}
          </div>

          {/* Selected Value Chips */}
          {shouldShowChips && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid={testId ? `${testId}-chips` : undefined}>
              {selectedLabels.map((label, index) => (
                <Chip key={`${label}-${index}`} chipVariant={VariantType.Outlined} className="px-3" adornment={label} />
              ))}
            </div>
          )}
        </div>

        {/* Side Panel for Form Controls (non-switch inputs) */}
        {!besideTitle && children && (
          <div className={sidePanelClasses}>
            <div
              className="w-full sm:max-w-sm"
              aria-labelledby={testId && typeof title === 'string' ? `${testId}-label` : undefined}
            >
              {children}
            </div>
          </div>
        )}
      </Cards>
    );
  },
);

FieldCard.displayName = 'FieldCard';
